import React, { useState } from 'react';
import { toast } from 'sonner';
import { History, PenLine, RefreshCw, ShieldCheck, Table2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'components/ui/sheet';
import RunProgress from './RunProgress';
import DocPreview from './DocPreview';
import { ModelSelect } from './StageVideo';
import {
  blogGenGetVersion, blogGenRestoreVersion, blogGenRetable, blogGenVerify, blogGenWrite,
} from '../../../services/api';
import { ACTIVE, errMsg, fmtDate } from './util';

const EFFORTS = ['low', 'medium', 'high', 'xhigh'];

export default function StageWrite({
  post, models, runs, onRunStarted, templateOverride, onState, verifyRun, sectionRun, isAdmin, onJumpTs,
}) {
  const [model, setModel] = useState(null);
  const [effort, setEffort] = useState(null);
  const [verify, setVerify] = useState(true);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionPreview, setVersionPreview] = useState(null);
  const writeRun = runs.write;
  const active = (writeRun && ACTIVE.has(writeRun.status)) || (sectionRun && ACTIVE.has(sectionRun.status));
  const report = post.verify_report;
  const cards = post.player_cards || [];
  const blocked = cards.filter((c) => c.role !== 'excluded' && c.needs_review);

  const write = async () => {
    try {
      const r = await blogGenWrite({ post_id: post.id, model, effort, template_override: templateOverride || null, verify });
      onRunStarted('write', r.run_id);
      if (r.reused) toast.info('A write is already running for this exact input');
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const runVerify = async () => {
    try {
      const r = await blogGenVerify(post.id);
      onRunStarted('verify', r.run_id);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const retable = async () => {
    try {
      onState(await blogGenRetable(post.id));
      toast.success('Tables re-rendered from fresh data');
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const openVersion = async (v) => {
    try {
      setVersionPreview(await blogGenGetVersion(post.id, v.id));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const restore = async (v) => {
    try {
      onState(await blogGenRestoreVersion(post.id, v.id));
      setVersionPreview(null);
      setVersionsOpen(false);
      toast.success('Version restored');
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const hasDoc = post.content?.content?.length > 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <Label>Model</Label>
          <ModelSelect models={models} value={model} onChange={setModel} />
        </div>
        <div>
          <Label>Effort</Label>
          <Select value={effort || '__default'} onValueChange={(v) => setEffort(v === '__default' ? null : v)}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__default">Template default</SelectItem>
              {EFFORTS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Check after writing</Label>
          <Select value={verify ? 'yes' : 'no'} onValueChange={(v) => setVerify(v === 'yes')}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="yes">Yes (Pass 3)</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button className="w-full" disabled={active || blocked.length > 0} onClick={write}>
            <PenLine /> {hasDoc ? 'Regenerate all' : 'Write the post'}
          </Button>
        </div>
      </div>
      {blocked.length > 0 && (
        <p className="text-token-base text-warning">
          Writing is blocked until {blocked.length} player{blocked.length > 1 ? 's' : ''} on the Players step {blocked.length > 1 ? 'are' : 'is'} confirmed: {blocked.slice(0, 5).map((c) => c.web_name).join(', ')}
        </p>
      )}
      {templateOverride && <Badge variant="warning">Using a per-post template override</Badge>}

      <RunProgress run={writeRun} label="Writing the post"><Button size="sm" onClick={write}>Retry</Button></RunProgress>
      {sectionRun && <RunProgress run={sectionRun} label="Rewriting a section" />}

      {hasDoc && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={runVerify} disabled={verifyRun && ACTIVE.has(verifyRun.status)}><ShieldCheck /> Re-run check</Button>
          <Button size="sm" variant="outline" onClick={retable}><Table2 /> Re-render tables</Button>
          <Button size="sm" variant="outline" onClick={() => setVersionsOpen(true)}><History /> Versions</Button>
        </div>
      )}
      {verifyRun && ACTIVE.has(verifyRun.status) && <RunProgress run={verifyRun} label="Checking the draft" />}

      {report && (
        <div className="rounded-md border border-borderGrey p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="text-token-base font-medium">Faithfulness check</span>
            <Badge variant="success">{report.counts?.supported ?? 0} supported</Badge>
            <Badge variant="warning">{report.counts?.unsupported ?? 0} unsupported</Badge>
            <Badge variant="destructive">{report.counts?.contradicted ?? 0} contradicted</Badge>
            {report.unknown_players?.length > 0 && <Badge variant="destructive">Unknown players: {report.unknown_players.join(', ')}</Badge>}
          </div>
          <p className="mb-2 text-token-base text-muted-foreground">{report.summary}</p>
          <div className="max-h-[260px] space-y-1 overflow-y-auto">
            {[...(report.claims || [])]
              .filter((c) => c.status !== 'supported')
              .sort((a, b) => (a.status === 'contradicted' ? -1 : 1) - (b.status === 'contradicted' ? -1 : 1))
              .map((c, i) => (
                <div key={i} className="rounded border border-borderGrey/60 px-2 py-1 text-token-base">
                  <Badge variant={c.status === 'contradicted' ? 'destructive' : 'warning'} className="mr-1">{c.status}</Badge>
                  <span className="text-muted-foreground">{c.section}:</span> {c.claim}
                  {c.evidence && (
                    <div className="text-[11px] text-muted-foreground">
                      ← {c.evidence.split(/(\[\d{1,2}:\d{2}(?::\d{2})?\])/).map((part, j) =>
                        /^\[\d/.test(part) ? <button key={j} type="button" className="text-primary underline" onClick={() => onJumpTs(part)}>{part}</button> : part)}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}

      <Sheet open={versionsOpen} onOpenChange={(o) => { setVersionsOpen(o); if (!o) setVersionPreview(null); }}>
        <SheetContent className="w-[560px] overflow-y-auto sm:max-w-[560px]">
          <SheetHeader><SheetTitle>Content versions</SheetTitle></SheetHeader>
          <div className="mt-3 space-y-2">
            {(post._versions || []).map((v) => (
              <div key={v.id} className="flex items-center justify-between rounded border border-borderGrey px-2 py-1.5 text-token-base">
                <span>{v.label || 'Version'} · <span className="text-muted-foreground">{fmtDate(v.created_at)}</span></span>
                <span className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7" onClick={() => openVersion(v)}>View</Button>
                  <Button size="sm" variant="outline" className="h-7" onClick={() => restore(v)}>Restore</Button>
                </span>
              </div>
            ))}
            {(post._versions || []).length === 0 && <p className="text-muted-foreground">No versions yet</p>}
          </div>
          {versionPreview && (
            <div className="mt-4 max-h-[50vh] overflow-y-auto rounded border border-borderGrey p-3">
              <p className="mb-2 text-token-base font-medium">{versionPreview.label}</p>
              <DocPreview doc={versionPreview.content} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export function RewritePopoverBody({ onSubmit, busy }) {
  const [custom, setCustom] = useState('');
  const presets = [
    ['Shorter', 'Make it shorter and tighter, keep the verdict.'],
    ['More stats', 'Use more of the stats provided to back the argument.'],
    ['Closer to the notes', 'Stick more closely to the presenter notes; drop anything not in them.'],
  ];
  return (
    <div className="space-y-2">
      {presets.map(([l, t]) => (
        <Button key={l} size="sm" variant="outline" className="w-full justify-start" disabled={busy} onClick={() => onSubmit(t)}>{l}</Button>
      ))}
      <Textarea rows={2} value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Or tell it what to change…" />
      <Button size="sm" className="w-full" disabled={busy || !custom.trim()} onClick={() => onSubmit(custom.trim())}><RefreshCw /> Rewrite</Button>
    </div>
  );
}
